import { useMemo, useEffect, useState } from 'react';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  selectWorkingProjectRegistrationSettings,
  selectProjectChannel,
  selectProjectName,
} from '@/store/slices/project/selectors';
import { fetchPasscodeLists } from '@/store/slices/passcode-lists';
import { generateDefaultAudienceCardProps } from './default-passcode-lists';
import { AUDIENCE_CARD_TYPE, AudienceCardProps, GateTypes, PasscodeList } from '@/types/registration.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import {
  selectLoadingPasscodeLists,
  selectMemberPasscodeListsLoading,
  selectPasscodeLists,
} from '@/store/slices/passcode-lists/selectors';
import AudiencesFilters from './components/audiences-filters';
import SelectListModal from './components/listsv2/select-list-modal';
import AudiencesSkeletonLoader from '@ui/skeleton-loader/custom/audiences-skeleton-loader';
import AudiencesStatsRow from './components/audiences-stats-row';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Link from '@ui/links/link';
import useAudiencesLayoutType from './hooks/use-audiences-layout-type';
import AudiencesGridView from './components/audiences-grid-view';
import AudiencesListView from './components/audiences-list-view';
import CreateListModal from './components/listsv2/create-list-modal';
import NoAudienceLists from './components/no-audience-lists';
import { getDisplayName, downloadUsers, downloadPasscodeList, removePasscodeList } from './utils';
import { useProjectUuidParam } from '@/utils/use-project-uuid-param';
import toastTrigger from '@ui/toast/toast-trigger';
import AudienceBulkActions from './components/listsv2/audience-bulk-actions';
import { EToastTypes } from '@ui/toast/toast.types';
import useLanguageParams from '@/utils/use-language-params';
import { useLoadEventRegistrantCounts } from './hooks/use-load-event-registrant-counts';

const Audiences = () => {
  const dispatch = useAppDispatch();

  const passcodeLists = useAppSelector(selectPasscodeLists);
  const memberPasscodeListsLoading = useAppSelector(selectMemberPasscodeListsLoading);
  const loadingPasscodeLists = useAppSelector(selectLoadingPasscodeLists);
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const channel = useAppSelector(selectProjectChannel);
  const projectName = useAppSelector(selectProjectName);
  const projectUuid = useProjectUuidParam();
  const language = useLanguageParams();
  const navigate = useNavigate();

  const { layout } = useAudiencesLayoutType();

  const [selectedPasscodeListIds, setSelectedPasscodeListIds] = useState<number[]>([]);
  const [selectedRegistrantLists, setSelectedRegistrantLists] = useState<AUDIENCE_CARD_TYPE[]>([]);

  const handleCardCheckboxChange = (
    checked: boolean,
    audienceCardType: AUDIENCE_CARD_TYPE,
    passcodeListId?: number
  ) => {
    if (audienceCardType === AUDIENCE_CARD_TYPE.PASSCODE_LIST && passcodeListId !== undefined) {
      setSelectedPasscodeListIds((prev) =>
        checked ? [...prev, passcodeListId] : prev.filter((id) => id !== passcodeListId)
      );
    } else {
      setSelectedRegistrantLists((prev) =>
        checked ? [...prev, audienceCardType] : prev.filter((type) => type !== audienceCardType)
      );
    }
  };

  const handleSelectAll = (select: boolean, cards: AudienceCardProps[]) => {
    const passcodeIds = cards
      .filter((c) => c.audienceCardType === AUDIENCE_CARD_TYPE.PASSCODE_LIST && c.passcodeListId !== undefined)
      .map((c) => c.passcodeListId!);
    const registrantTypes = cards
      .filter((c) => c.audienceCardType !== AUDIENCE_CARD_TYPE.PASSCODE_LIST)
      .map((c) => c.audienceCardType);

    if (select) {
      setSelectedPasscodeListIds((prev) => [...new Set([...prev, ...passcodeIds])]);
      setSelectedRegistrantLists((prev) => [...new Set([...prev, ...registrantTypes])]);
    } else {
      setSelectedPasscodeListIds((prev) => prev.filter((id) => !passcodeIds.includes(id)));
      setSelectedRegistrantLists((prev) => prev.filter((type) => !registrantTypes.includes(type)));
    }
  };

  const handleBulkRemove = () => {
    if (!registrationSettings || selectedPasscodeListIds.length === 0) return;
    removePasscodeList({ registrationSettings, passcodeListIds: selectedPasscodeListIds, dispatch });
    handleClear();
  };

  const handleBulkDownload = async () => {
    if (!channel || !projectUuid) return;

    try {
      await Promise.all([
        ...selectedRegistrantLists.map((audienceCardType) =>
          downloadUsers({ channel, projectUuid: projectUuid, audienceCardType, projectName })
        ),
        ...selectedPasscodeListIds.flatMap((id) => {
          const passcodeList = passcodeLists.find((l) => l.passcode_list === id);
          if (!passcodeList) return [];
          return [downloadPasscodeList({ passcodeList, name: getDisplayName(passcodeList.name) })];
        }),
      ]);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Your audience list failed to generate';

      toastTrigger('Download failed', {
        description: message,
        type: EToastTypes.error,
        autoClose: 7000,
      });
    }
    handleClear();
  };

  const handleCreateCampaign = () => {
    // TODO: when campaigns are working, this will pass the passcode lists to campaigns
    navigate(`/project/edit/${projectUuid}/${language}/campaigns`);
  };

  const handleClear = () => {
    setSelectedPasscodeListIds([]);
    setSelectedRegistrantLists([]);
  };

  const [showListsModal, setShowListsModal] = useState(false);
  const [showAddNewListModal, setShowAddNewListModal] = useState(false);
  const [attachedPasscodeLists, setAttachedPasscodeLists] = useState<PasscodeList[]>([]);
  const [unattachedPasscodeLists, setUnattachedPasscodeLists] = useState<PasscodeList[]>([]);

  const [filteredAudienceCardProps, setFilteredAudienceCardProps] = useState<AudienceCardProps[]>([]);

  const params = useParams();
  const activeView = params['*'];

  const { counts: statusCounts, loading: statusCountsLoading } = useLoadEventRegistrantCounts();

  // const { statusCounts, loading: statusCountsLoading } = useRegistrantStatusCounts(projectUuid || '');
  const [searchParams] = useSearchParams();
  const isLoading = memberPasscodeListsLoading || loadingPasscodeLists || statusCountsLoading;

  const defaultAudienceCardProps = useMemo(() => {
    return statusCounts ? generateDefaultAudienceCardProps(statusCounts, projectName) : [];
  }, [projectName, statusCounts]);

  useEffect(() => {
    if (channel) {
      dispatch(fetchPasscodeLists(channel));
    }
  }, [channel, dispatch]);

  useEffect(() => {
    const attachedPasscodeListIds: number[] =
      registrationSettings?.requirements?.[GateTypes.passcode_list]?.flatMap(
        (requirement) => requirement.passcodeList ?? []
      ) ?? [];

    const { attachedLists, unattachedLists } = passcodeLists.reduce(
      (both, list) => {
        if (list.passcode_list && attachedPasscodeListIds.includes(list.passcode_list)) {
          both.attachedLists.push(list);
        } else {
          both.unattachedLists.push(list);
        }
        return both;
      },
      { attachedLists: [] as PasscodeList[], unattachedLists: [] as PasscodeList[] }
    );

    setAttachedPasscodeLists(attachedLists);
    setUnattachedPasscodeLists(unattachedLists);
  }, [passcodeLists, registrationSettings]);

  const search = searchParams.get('search') || '';

  useEffect(() => {
    const passcodeListAudienceCardProps: AudienceCardProps[] = attachedPasscodeLists.map((passcodeList) => {
      return {
        name: getDisplayName(passcodeList.name),
        subHeader: passcodeList.description || '',
        audienceCardType: AUDIENCE_CARD_TYPE.PASSCODE_LIST,
        passcodeListId: passcodeList.passcode_list,
        passcodeList: passcodeList,
        member_count: passcodeList.member_count,
        created_date: passcodeList.created_at,
      };
    });

    const allAudienceCardProps: AudienceCardProps[] = [...defaultAudienceCardProps, ...passcodeListAudienceCardProps];

    const filteredAudienceCardProps = allAudienceCardProps.filter((filter) =>
      filter.name?.toLowerCase().includes(search.toLowerCase())
    );

    setFilteredAudienceCardProps(filteredAudienceCardProps || []);
  }, [attachedPasscodeLists, defaultAudienceCardProps, search, passcodeLists]);

  return (
    <>
      <Container
        className="flex-column gap-20"
        minHeight="calc(100vh - 500px)"
        padding="0 var(--main-content-horizontal-padding)"
      >
        <Container className="flex fd-row jc-space-between">
          <Text tag="h3" className="H3" color="var(--gray-100)">
            Audiences
          </Text>

          <Link
            design="LocalBttnSec"
            to={`/project/edit/${projectUuid}/${language}/audiences/all`}
            className="flex fd-row ai-center gap-6 padding-v-6 padding-h-16"
            borderRadius="100px"
          >
            <Icon name={ICONS.USERS} size={12} />
            <Text className="L2">All Profiles</Text>
          </Link>
        </Container>

        <Container
          className="flex ai-flex-start position-absolute z-10 top-0 left-0 right-0 bottom-0"
          opacity={isLoading ? 1 : 0}
          style={{
            visibility: isLoading ? 'visible' : 'hidden',
            transition: 'opacity 300ms ease-in-out, visibility 300ms ease-in-out',
            pointerEvents: isLoading ? 'auto' : 'none',
            position: 'absolute',
            top: 48,
          }}
        >
          <AudiencesSkeletonLoader />
        </Container>

        <AudiencesStatsRow />

        <Container className="flex fd-row jc-space-between">
          <Text tag="h4" className="H4" color="var(--gray-100)">
            All Lists
          </Text>

          <AudiencesFilters
            expandedSearchWidth="280px"
            onAddClick={() => setShowListsModal(true)}
            onAddNewClick={() => setShowAddNewListModal(true)}
            activeView={activeView}
          />
        </Container>
        <AudienceBulkActions
          selectedPasscodeListIds={selectedPasscodeListIds}
          selectedRegistrantLists={selectedRegistrantLists}
          onClearSelectedLists={handleClear}
          onDownload={handleBulkDownload}
          onRemove={handleBulkRemove}
          onCreateCampaign={handleCreateCampaign}
        />
        <OptionalComponent display={filteredAudienceCardProps.length > 0}>
          <OptionalComponent display={layout === 'grid'}>
            <AudiencesGridView
              isLoading={isLoading}
              filteredAudienceCardProps={filteredAudienceCardProps}
              selectedPasscodeListIds={selectedPasscodeListIds}
              selectedRegistrantLists={selectedRegistrantLists}
              onCheckboxChange={handleCardCheckboxChange}
              onCreateCampaign={handleCreateCampaign}
            />
          </OptionalComponent>
          <OptionalComponent display={layout === 'list'}>
            <AudiencesListView
              isLoading={isLoading}
              filteredAudienceCardProps={filteredAudienceCardProps}
              selectedPasscodeListIds={selectedPasscodeListIds}
              selectedRegistrantLists={selectedRegistrantLists}
              onCheckboxChange={handleCardCheckboxChange}
              onSelectAll={handleSelectAll}
            />
          </OptionalComponent>
        </OptionalComponent>
        <OptionalComponent display={filteredAudienceCardProps.length === 0 && !isLoading}>
          <NoAudienceLists />
        </OptionalComponent>
      </Container>
      <SelectListModal open={showListsModal} setOpen={setShowListsModal} passcodeLists={unattachedPasscodeLists} />
      <CreateListModal open={showAddNewListModal} setOpen={setShowAddNewListModal} />
    </>
  );
};

export default Audiences;
